import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { fmtDateShortParis } from "../lib/date";
import { Calendar } from "lucide-react";
import { Button } from "../components/ui/button";

const TYPES = [
  { id: "all", label: "Tous" },
  { id: "afterwork", label: "Afterwork" },
  { id: "atelier_cocktail", label: "Ateliers Cocktail" },
  { id: "atelier_rhum", label: "Ateliers Rhum" },
];

export default function Events() {
  const [items, setItems] = useState([]);
  const [params, setParams] = useSearchParams();
  const active = params.get("type") || "all";

  useEffect(() => {
    const url = active === "all" ? "/events" : `/events?type=${active}`;
    api.get(url).then(({ data }) => setItems(data)).catch(() => {});
  }, [active]);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24" data-testid="events-page">
      <div className="mb-12">
        <div className="text-xs uppercase tracking-[0.3em] text-primary mb-3">Notre agenda</div>
        <h1 className="font-display text-5xl lg:text-6xl mb-4">Événements & Ateliers</h1>
        <p className="text-muted-foreground max-w-2xl">Afterworks, masterclass cocktails, ateliers rhum arrangé — réservez votre place pour nos prochaines expériences.</p>
      </div>

      <div className="flex flex-wrap gap-2 mb-10" data-testid="event-filters">
        {TYPES.map((t) => (
          <Button
            key={t.id}
            variant={active === t.id ? "default" : "outline"}
            size="sm"
            className={active === t.id ? "bg-primary text-primary-foreground rounded-full" : "rounded-full"}
            onClick={() => setParams(t.id === "all" ? {} : { type: t.id })}
            data-testid={`filter-${t.id}`}
          >
            {t.label}
          </Button>
        ))}
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.length === 0 && <div className="text-muted-foreground col-span-3">Aucun événement disponible pour cette catégorie.</div>}
        {items.map((e) => (
          <Link key={e.id} to={`/evenements/${e.id}`} className={`group rounded-xl overflow-hidden bg-card border border-border hover-lift relative ${e.seats_available <= 0 ? "opacity-70" : ""}`} data-testid={`event-card-${e.id}`}>
            {e.seats_available <= 0 && (
              <div className="absolute top-3 right-3 z-10 rounded-full bg-rose-500/95 text-white px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] shadow-lg" data-testid={`event-sold-out-${e.id}`}>
                Complet
              </div>
            )}
            {e.image && (
              <div className="aspect-[4/3] overflow-hidden">
                <img src={e.image} alt={e.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
            )}
            <div className="p-6">
              <div className="text-xs uppercase tracking-[0.2em] text-primary mb-2">
                {TYPES.find((t) => t.id === e.type)?.label || e.type}
              </div>
              <h3 className="font-display text-2xl mb-2">{e.title}</h3>
              <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{e.description}</p>
              <div className="flex items-center justify-between text-sm border-t border-border pt-4">
                <span className="text-muted-foreground inline-flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {fmtDateShortParis(e.date)}
                </span>
                <span className="gold-text font-medium">{e.price.toFixed(0)} €</span>
              </div>
              <div className="mt-2 text-xs">
                {e.seats_available <= 0 ? (
                  <span className="text-rose-400 font-semibold uppercase tracking-[0.15em]">Complet</span>
                ) : e.seats_available <= 3 ? (
                  <span className="text-orange-400 font-semibold animate-pulse" data-testid={`event-low-seats-${e.id}`}>
                    ⚡ Plus que {e.seats_available} place{e.seats_available > 1 ? "s" : ""} !
                  </span>
                ) : (
                  <span className="text-muted-foreground">{e.seats_available} place(s) restante(s)</span>
                )}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
